import React, { useEffect, useCallback } from 'react';
import styles from 'feature/street-parking/index.module.sass';
import { useHistory, useLocation } from 'react-router-dom';
import {
  useStreetParkingContext,
  PICK_PAYING_BILLS,
  PICK_ALL_PAYING_BILLS,
  RESET_PAY_STATE,
} from 'feature/street-parking/use-street-parking';
import qs from 'query-string';
import Card from 'components/card';
import Button from 'components/button';
import LoadingDialog from 'components/loading-dialog';
import AppMessage from 'components/app-message';
import empty from 'assets/icon_empty_receipt.svg';
import Checkbox from 'components/checkbox';
import { PATH } from 'config';
import Info from 'components/info';
import amplitude from 'service/amplitude';
import StreetParkingLayout from 'layout/street-parking';
import EmptyState from 'components/empty-state';

const StreetParkingBills = () => {
  const { state, dispatch, findCityNameById, getStreetParkingBills, payStreetParkingBills } = useStreetParkingContext();
  const history = useHistory();
  const location = useLocation();
  const params = qs.parse(location.search);
  const cityParams = params.city;
  const plateParams = params.plate;

  useEffect(() => {
    getStreetParkingBills(cityParams, plateParams);
  }, [cityParams, plateParams, getStreetParkingBills]);

  const pickPayingBills = useCallback(
    bill => {
      dispatch({
        type: PICK_PAYING_BILLS,
        payload: bill.code,
      });
    },
    [dispatch]
  );

  const pickAllPayingBills = useCallback(() => {
    dispatch({
      type: PICK_ALL_PAYING_BILLS,
    });
  }, [dispatch]);

  const CheckItem = ({ id, name, isChecked }) => {
    if (state.payInFull) {
      return <span className={styles.amountLabel}>繳費金額</span>;
    }
    return <Checkbox id={id} name={name} isChecked={isChecked} />;
  };

  const BillItem = ({ bill, isChecked, name }) => {
    return (
      <div className={styles['card']} onClick={() => pickPayingBills(bill)}>
        <Card>
          <div className={styles['card-top']}>
            <div className={styles.code}>{bill.code}</div>
            <div className={styles.date}>
              <span>停車日期</span>
              <span>{bill.parkingDate}</span>
            </div>
            <div className={styles.expire}>
              <span>繳款期限</span>
              <span>{bill.expiresDate}</span>
            </div>
          </div>
          <div className={styles['card-bottom']}>
            <CheckItem id={bill.code} name={name} isChecked={isChecked} />
            <div className={styles.amount}>${Math.round(bill.amount)}</div>
          </div>
        </Card>
      </div>
    );
  };

  const Panel = () => {
    if (state.streetParkingBills.length === 0) return <></>;

    const CheckAll = () => {
      if (state.payInFull) {
        return <span className={styles.allAmountLabel}>繳費金額</span>;
      }
      return (
        <>
          <Checkbox
            id="selectAll"
            name="全選"
            isChecked={state.selectAllChecked}
            onCheckChanged={() => {
              pickAllPayingBills();
            }}
          />
          <span>（最多 10 筆）</span>
        </>
      );
    };

    return (
      <div className={styles.panel}>
        <div className={styles.select}>
          <div className={styles['select-all']}>
            <CheckAll />
          </div>
          <div>
            已選 {state.currentSelectBillsCode.length} 筆，共 <span>${state.totalAmount}</span>
          </div>
        </div>
        <Button
          style={{ minWidth: 120 }}
          disabled={state.streetParkingBillsButtonDisable}
          onClick={() => {
            amplitude.onStreetParking_inquire_complete_onClick();
            payStreetParkingBills(cityParams, plateParams);
          }}
        >
          繳費
        </Button>
      </div>
    );
  };

  const Board = () => {
    const { searchBillBoard } = state;

    if (!searchBillBoard) return <></>;

    const { attributes } = searchBillBoard;
    return (
      <Info customStyle={{ background: attributes.backgroundColor, color: attributes.titleColor }}>
        {attributes.title}
      </Info>
    );
  };

  const BillsList = () => {
    let billsList = state.streetParkingBills;

    if (billsList.length === 0) {
      return <EmptyState text="路停繳費單" icon={empty} />;
    }

    return (
      <div className={styles['bills-list']}>
        {billsList.map(bill => {
          const name = state.currentSelectBillsCode.indexOf(bill.code) !== -1 ? '已選取' : '選取';
          const isChecked = state.currentSelectBillsCode.indexOf(bill.code) !== -1;
          return <BillItem key={bill.code} bill={bill} isChecked={isChecked} name={name} />;
        })}
      </div>
    );
  };

  return (
    <>
      <AppMessage
        type={state.payState}
        isShow={state.payStateShowing}
        onDismiss={() => {
          if (state.orderUrl) {
            const payAllBills = state.currentSelectBillsCode.length === state.streetParkingBills.length;
            history.push({
              pathname: PATH.ORDER_DETAIL,
              search: `url=${state.orderUrl}`,
              state: { from: location.pathname, payAllBills },
            });
          }
          dispatch({ type: RESET_PAY_STATE });
        }}
      >
        {state.payMsg}
      </AppMessage>
      <LoadingDialog isShow={state.loading} />
      <Board />
      <StreetParkingLayout className={styles.bills}>
        <div className={styles.title}>
          繳費車牌：{plateParams}
          <br />
          繳款對象：{findCityNameById(cityParams)}路邊停車費
        </div>
        <BillsList />
        <Panel />
      </StreetParkingLayout>
    </>
  );
};

export default StreetParkingBills;
